import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError, AxiosResponse } from 'axios';
import { DefaultTFuncReturn, TFunction } from 'i18next';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import {
  PostProductionAdd200,
  ProductionBasicInfo,
  ProductionDetail,
  ProductionEvent,
  ProductionMasters,
  ProductionParentChild,
  ProductionPreparationItem,
  TreeCompany,
} from '../../../../api/model';
import {
  usePostProductionAdd,
  usePostProductionCancel,
  usePostProductionLock,
  usePostProductionOrderSheetsDownload,
  usePostProductionSave,
} from '../../../../api/productions/productions';
import { useDefaultOptions, useOptionsForBinary } from '../../../../hooks/api';
import { postWithKeepAlive } from '../../../../utils/api';
import yup from '../../../../utils/custom-yup';
import { executeDownload } from '../../../../utils/download';
import NoDataComponent from '../../../atoms/NoDataComponent';
import AnotherTabLockingErrorModal from '../../../modules/AnotherTabLockingErrorModal';
import BlobErrorModal from '../../../modules/BlobErrorModal';
import ConfirmModal from '../../../modules/ConfirmModal';
import ErrorModal from '../../../modules/ErrorModal';
import OkModal from '../../../modules/OkModal';
import { getOptions } from '../../../modules/OrganizationTreeMultiSelect';
import SlideToggle from '../../../modules/SlideToggle';
import SpinnerModal from '../../../modules/SpinnerModal';
import YesNoModal from '../../../modules/YesNoModal';
import {
  CancelButton,
  DatePickerGroup,
  InputGroup,
  MultiSelectGroup,
  OrganizationTreeMultiSelectGroup,
  SingleSelectGroup,
} from './FormElements';

/**
 * 発注書出力ドロップダウンの選択肢
 * @param divisinId
 * @param t
 * @returns
 */
const getOutputOptions = (divisinId: number, t: TFunction) => {
  const options: { value: number; label: string }[] = [];
  switch (divisinId) {
    case 1:
    case 2:
      options.push({ value: 1, label: t('OM作成発注書_制作管理表(PS)') });
      break;
    case 3:
      options.push({ value: 2, label: t('NAVIOM作成発注書_制作管理表(PS)') });
      break;
    case 4:
      options.push({ value: 4, label: 'dOM_発注書_制作管理表' });
      break;
    case 5:
      options.push({ value: 3, label: t('webom制作管理表') });
      break;
    case 6:
      options.push({ value: 5, label: t('イラスト発注書') });
      break;
    default:
      options.push({ value: 6, label: t('翻訳編集発注書') });
      break;
  }
  options.push(
    { value: 7, label: t('印刷物仕様書') },
    { value: 8, label: t('改訂版OM版下発注書') },
    { value: 10, label: t('差分調査') },
    { value: 9, label: t('設変リスト確認作業') }
  );
  return options;
};

interface SortableThProps {
  label: DefaultTFuncReturn;
  sortKey: string;
  onSort: (sortKey: string) => void;
}

/**
 * 制作管理データの親子関係リストのヘッダー
 * @param param0
 * @returns
 */
function SortableTh({ label, sortKey, onSort }: SortableThProps) {
  return (
    <th>
      <button type="button" className="text-button" onClick={() => onSort(sortKey)}>
        {label}
        <i className="fa-solid fa-sort" />
      </button>
    </th>
  );
}

interface ParentChildListProps {
  parentChildList: ProductionParentChild[];
}

/**
 * 制作管理表タブの親子関係リスト
 * @param param0
 * @returns
 */
function ParentChildList({ parentChildList }: ParentChildListProps) {
  // 言語情報
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // 一覧のソート
  const [sortInfo, setSortInfo] = useState({
    key: 'parent_child',
    isDisc: false,
  });

  // ソートする関数
  const onSort = useCallback(
    (sortKey: string) => {
      setSortInfo((pre) => ({
        key: sortKey,
        isDisc: pre.key === sortKey && !pre.isDisc,
      }));
    },
    [setSortInfo]
  );

  // ソート後の一覧
  const sortedList = useMemo(
    () =>
      parentChildList.sort((a, b) => {
        const aAny = a as { [key: string]: string };
        const bAny = b as { [key: string]: string };
        return (aAny[sortInfo.key] > bAny[sortInfo.key] ? 1 : -1) * (sortInfo.isDisc ? -1 : 1);
      }),
    [parentChildList, sortInfo]
  );

  return (
    <div className="basicdatafr3">
      <table className="table12 mt30">
        <tbody>
          {/* ヘッダー部分 */}
          <tr>
            <SortableTh label={t('親子')} sortKey="production_no" onSort={onSort} />
            <SortableTh label={t('資料管理No')} sortKey="production_division_name" onSort={onSort} />
            <SortableTh label={t('種別')} sortKey="title" onSort={onSort} />
            <SortableTh label={t('資料名')} sortKey="maker_company_name" onSort={onSort} />
            <SortableTh label={t('言語')} sortKey="language_name" onSort={onSort} />
            <SortableTh label={t('原作メーカ')} sortKey="maker_company_name" onSort={onSort} />
            <SortableTh label={t('進捗状況')} sortKey="production_status_name" onSort={onSort} />
            <SortableTh label={t('校了日')} sortKey="proofreading_on" onSort={onSort} />
          </tr>
          {/* 一覧 */}
          {sortedList.map((x) => (
            <tr key={x.production_id}>
              <td>{x.parent_child}</td>
              <td>
                <Link
                  to={`/${language}/productions/edit/${x.production_id?.toString() ?? ''}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {x.production_no}
                </Link>
              </td>
              <td>{x.production_division_name}</td>
              <td>{x.title}</td>
              <td>{x.language_name}</td>
              <td>{x.maker_company_name}</td>
              <td>{x.production_status_name}</td>
              <td>{x.proofreading_on}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const BASIC_INFO_INPUT_NAMES = {
  productionNo: 'production_no',
  title: 'title',
  salesMy: 'sales_my',
  developmentMy: 'development_my',
  modelName: 'model_name',
  code: 'code',
  doorIds: 'door_ids',
  engineIds: 'engine_ids',
  destinationIds: 'destination_ids',
  factoryIds: 'factory_ids',
  makerCompanyId: 'maker_company_id',
  developmentScaleId: 'development_scale_id',
  supplimentaryInfomation: 'supplimentary_infomation',
  modelIds: 'model_ids', // UI の name 属性として使用
  modelUserIds: 'model_user_ids',
  secretManagementNumber: 'secret_management_number',
  languageId: 'language_id',
  developmentBaseIds: 'development_base_ids',
  orderDetail: 'order_detail',
  productionProcessId: 'production_process_id',
  editingMethod: 'editing_method',
  baseProductionNo: 'base_production_no',
  otherLanguageId: 'other_language_id',
  otherLanguageLocal: 'other_language_local',
  translationDifferenceContentId: 'translation_difference_content_id',
  otherLanguageIllustId: 'other_language_illust_id',
  threeDimensionsIllustId: 'three_dimensions_illust_id',
  deliveryPdfSize: 'delivery_pdf_size',
  confidentialDocumentTrustPermission: 'confidential_document_trust_permission',
  dmcNo: 'dmc_no',
  remarks: 'remarks',
  originalWorkIds: 'original_work_ids', // UI の name 属性として使用
  originalWorkUserIds: 'original_work_user_ids',
  scheduleSharingIds: 'schedule_sharing_Ids', // UI の name 属性として使用
  scheduleSharingUserIds: 'schedule_sharing_user_ids', // 実際に送信するときの値
  scheduleShareringCompanyIds: 'schedule_sharing_company_ids', // 実際に送信するときの値
  parentProductionNo: 'parent_production_no',
  countTarget: 'count_target',
  correctionBeforeProofreading: 'correction_before_proofreading',
};

const EVENT_INPUT_NAMES = {
  productionId: 'production_id',
  kickOff: 'kick_off',
  serviceStudy: 'service_study',
  developmentDepartmentCheck: 'development_department_check',
  subsidaryCheck: 'subsidary_check',
  vehicleCoverage: 'vehicle_coverage',
  validation: 'validation',
  slipDueOn: 'slip_due_on',
  materialProvisionOn: 'material_provision_on',
  machineConfirmationStartOn: 'machine_confirmation_start_on',
  dataItemCompletionOn: 'data_item_completion_on',
  dataSendingOn: 'data_sending_on',
  translationCompletionOn: 'translation_completion_on',
  scheduledDeliveryOn: 'scheduled_delivery_on',
  downloadLocation: 'download_location',
  g1PreparationEndOn: 'g1_preparation_end_on',
  g1ConfirmationEndOn: 'g1_confirmation_end_on',
  completionOn: 'completion_on',
  webReleaseOn: 'web_release_on',
};

const PREPARATION_ITEM_INPUT_NAMES = {
  productionId: 'production_id',
  documentPlanning: 'document_planning',
  productionSchedule: 'production_schedule',
  deliveryDestination: 'delivery_destination',
  bmsRegistration: 'bms_registration',
  printSpecification: 'print_specification',
  arrangementInstruction: 'arrangement_instruction',
  massProductionDrawing: 'mass_production_drawing',
  desiredDeliveryOn: 'desired_delivery_on',
  bodyIllustOrdering: 'body_illust_ordering',
  lightsIllustOrdering: 'lights_illust_ordering',
  otherLanguageIllustOrdering: 'other_language_illust_ordering',
  vinRegistration: 'vin_registration',
  webColorImage: 'web_color_image',
  audioNaviColorImage: 'audio_navi_color_image',
  webomTranslationSchedule: 'webom_translation_schedule',
  generalPurchasingSlipNumber: 'general_purchasing_slip_number',
  bmsSlipNumber: 'bms_slip_number',
};

interface FormValues extends ProductionBasicInfo, ProductionEvent, ProductionPreparationItem {
  model_ids?: string[] | undefined;
  original_work_ids?: string[] | undefined;
  schedule_sharing_ids?: string[] | undefined;
}

/**
 * 制作管理表のフォームの初期値を取得する
 * @param basicInfo
 * @param event
 * @param preperationItem
 * @returns
 */
const getFormValues = (
  basicInfo: ProductionBasicInfo,
  event: ProductionEvent,
  preperationItem: ProductionPreparationItem
): FormValues => {
  let scheduleSharingIds: string[] = [];
  if (basicInfo.schedule_sharing_user_ids != null) {
    scheduleSharingIds = [...scheduleSharingIds, ...basicInfo.schedule_sharing_user_ids.map((x) => `user_${x}`)];
  }
  if (basicInfo.schedule_sharing_company_ids != null) {
    scheduleSharingIds = [...scheduleSharingIds, ...basicInfo.schedule_sharing_company_ids.map((x) => `company_${x}`)];
  }
  return {
    ...basicInfo,
    ...event,
    ...preperationItem,
    model_ids: basicInfo.model_user_ids?.map((x) => `user_${x}`),
    original_work_ids: basicInfo.original_work_user_ids?.map((x) => `user_${x}`),
    schedule_sharing_ids: scheduleSharingIds,
  };
};

interface Props {
  productionId: number | null;
  selectedDivisionId: number;
  basicInfo: ProductionBasicInfo;
  event: ProductionEvent;
  preperationItem: ProductionPreparationItem;
  parentChildList: ProductionParentChild[];
  editModeTabIndex: number | undefined;
  masters: ProductionMasters;
  companyTrees: TreeCompany[];
  changeToEditMode: () => void;
  changeToViewMode: () => void;
  refetch: (() => Promise<void>) | undefined;
  reset: () => void;
}

export interface ProductionManagementTableTabHandler {
  submit: () => Promise<void>;
  refresh: () => void;
}

/**
 * 制作管理表タブ
 */
const ProductionManagementTableTab = forwardRef<ProductionManagementTableTabHandler, Props>(
  (
    {
      productionId,
      selectedDivisionId,
      basicInfo,
      event,
      preperationItem,
      parentChildList,
      editModeTabIndex,
      masters,
      companyTrees,
      changeToEditMode,
      changeToViewMode,
      refetch,
      reset: resetParent,
    },
    ref
  ) => {
    // 言語情報
    const {
      t,
      i18n: { language },
    } = useTranslation();

    // 編集モードかどうか
    const editable = useMemo(() => editModeTabIndex === 1, [editModeTabIndex]);

    // 画面遷移用オブジェクト
    const navigate = useNavigate();

    // 基本情報が開いているかどう
    const [open, setOpen] = useState(true);

    // フォームバリデーション
    const schema = yup.object({
      [BASIC_INFO_INPUT_NAMES.productionNo]: yup.string().half().max(20).required(),
      [BASIC_INFO_INPUT_NAMES.title]: yup.string().nullable().max(465),
      [BASIC_INFO_INPUT_NAMES.salesMy]: yup.string().nullable().max(6).required(),
      [BASIC_INFO_INPUT_NAMES.developmentMy]: yup.string().nullable().max(6).required(),
      [BASIC_INFO_INPUT_NAMES.modelName]: yup.string().nullable().max(100),
      [BASIC_INFO_INPUT_NAMES.code]: yup.string().nullable().max(10),
      [BASIC_INFO_INPUT_NAMES.doorIds]: yup.array().nullable().of(yup.number()),
      [BASIC_INFO_INPUT_NAMES.engineIds]: yup.array().nullable().of(yup.number()),
      [BASIC_INFO_INPUT_NAMES.destinationIds]: yup.array().nullable().of(yup.number()),
      [BASIC_INFO_INPUT_NAMES.factoryIds]: yup.array().nullable().of(yup.number()),
      [BASIC_INFO_INPUT_NAMES.makerCompanyId]: yup
        .number()
        .nullable()
        .transform((v) => (Number.isNaN(Number(v)) || Number(v) === 0 ? null : Number(v)))
        .default(null),
      [BASIC_INFO_INPUT_NAMES.developmentScaleId]: yup
        .number()
        .nullable()
        .transform((v) => (Number.isNaN(Number(v)) || Number(v) === 0 ? null : Number(v)))
        .default(null),
      [BASIC_INFO_INPUT_NAMES.supplimentaryInfomation]: yup.string().nullable().max(300),
      [BASIC_INFO_INPUT_NAMES.modelIds]: yup
        .array()
        .nullable()
        .of(yup.string().matches(/^user_.*$/)),
      [BASIC_INFO_INPUT_NAMES.secretManagementNumber]: yup.string().nullable().half().max(20),
      [BASIC_INFO_INPUT_NAMES.languageId]: yup
        .number()
        .required()
        .transform((v) => (Number.isNaN(Number(v)) || Number(v) === 0 ? null : Number(v))),
      [BASIC_INFO_INPUT_NAMES.developmentBaseIds]: yup.array().nullable().of(yup.number()),
      [BASIC_INFO_INPUT_NAMES.orderDetail]: yup.string().nullable().max(300),
      [BASIC_INFO_INPUT_NAMES.productionProcessId]: yup
        .number()
        .nullable()
        .transform((v) => (Number.isNaN(Number(v)) || Number(v) === 0 ? null : Number(v)))
        .default(null),
      [BASIC_INFO_INPUT_NAMES.editingMethod]: yup.string().nullable().max(5),
      [BASIC_INFO_INPUT_NAMES.baseProductionNo]: yup.string().nullable().half().max(20),
      [BASIC_INFO_INPUT_NAMES.otherLanguageId]: yup
        .number()
        .nullable()
        .transform((v) => (Number.isNaN(Number(v)) || Number(v) === 0 ? null : Number(v)))
        .default(null),
      [BASIC_INFO_INPUT_NAMES.otherLanguageLocal]: yup.string().nullable().max(100),
      [BASIC_INFO_INPUT_NAMES.translationDifferenceContentId]: yup
        .number()
        .nullable()
        .transform((v) => (Number.isNaN(Number(v)) || Number(v) === 0 ? null : Number(v)))
        .default(null),
      [BASIC_INFO_INPUT_NAMES.otherLanguageIllustId]: yup
        .number()
        .nullable()
        .transform((v) => (Number.isNaN(Number(v)) || Number(v) === 0 ? null : Number(v)))
        .default(null),
      [BASIC_INFO_INPUT_NAMES.threeDimensionsIllustId]: yup
        .number()
        .nullable()
        .transform((v) => (Number.isNaN(Number(v)) || Number(v) === 0 ? null : Number(v)))
        .default(null),
      [BASIC_INFO_INPUT_NAMES.deliveryPdfSize]: yup.string().nullable(),
      [BASIC_INFO_INPUT_NAMES.confidentialDocumentTrustPermission]: yup.string().nullable().half().max(10),
      [BASIC_INFO_INPUT_NAMES.dmcNo]: yup.string().nullable().half().max(20),
      [BASIC_INFO_INPUT_NAMES.remarks]: yup.string().nullable().max(1000),
      [BASIC_INFO_INPUT_NAMES.originalWorkIds]: yup
        .array()
        .nullable()
        .of(yup.string().matches(/^user_\d*$/)),
      [BASIC_INFO_INPUT_NAMES.scheduleSharingIds]: yup
        .array()
        .nullable()
        .of(yup.string().matches(/^user_\d*$|^company_\d*$/)),
      [BASIC_INFO_INPUT_NAMES.parentProductionNo]: yup.string().nullable().half().max(20),
      [BASIC_INFO_INPUT_NAMES.countTarget]: yup
        .number()
        .nullable()
        .transform((v) => (Number.isNaN(Number(v)) || Number(v) === 0 ? null : Number(v)))
        .default(null),
      [BASIC_INFO_INPUT_NAMES.correctionBeforeProofreading]: yup.string().nullable().half().max(3),
      [EVENT_INPUT_NAMES.kickOff]: yup
        .number()
        .nullable()
        .transform((v) => (Number.isNaN(Number(v)) || Number(v) === 0 ? null : Number(v)))
        .default(null),
      [EVENT_INPUT_NAMES.serviceStudy]: yup
        .number()
        .nullable()
        .transform((v) => (Number.isNaN(Number(v)) || Number(v) === 0 ? null : Number(v)))
        .default(null),
      [EVENT_INPUT_NAMES.developmentDepartmentCheck]: yup
        .number()
        .nullable()
        .transform((v) => (Number.isNaN(Number(v)) || Number(v) === 0 ? null : Number(v)))
        .default(null),
      [EVENT_INPUT_NAMES.subsidaryCheck]: yup
        .number()
        .nullable()
        .transform((v) => (Number.isNaN(Number(v)) || Number(v) === 0 ? null : Number(v)))
        .default(null),
      [EVENT_INPUT_NAMES.vehicleCoverage]: yup
        .number()
        .nullable()
        .transform((v) => (Number.isNaN(Number(v)) || Number(v) === 0 ? null : Number(v)))
        .default(null),
      [EVENT_INPUT_NAMES.validation]: yup
        .number()
        .nullable()
        .transform((v) => (Number.isNaN(Number(v)) || Number(v) === 0 ? null : Number(v)))
        .default(null),
      [EVENT_INPUT_NAMES.slipDueOn]: yup.string().date().nullable(),
      [EVENT_INPUT_NAMES.materialProvisionOn]: yup.string().date().nullable(),
      [EVENT_INPUT_NAMES.machineConfirmationStartOn]: yup.string().date().nullable(),
      [EVENT_INPUT_NAMES.dataItemCompletionOn]: yup.string().date().nullable(),
      [EVENT_INPUT_NAMES.dataSendingOn]: yup.string().date().nullable(),
      [EVENT_INPUT_NAMES.translationCompletionOn]: yup.string().date().nullable(),
      [EVENT_INPUT_NAMES.scheduledDeliveryOn]: yup.string().date().nullable(),
      [EVENT_INPUT_NAMES.downloadLocation]: yup.string().nullable().max(10),
      [EVENT_INPUT_NAMES.g1PreparationEndOn]: yup.string().date().nullable(),
      [EVENT_INPUT_NAMES.g1ConfirmationEndOn]: yup.string().date().nullable(),
      [EVENT_INPUT_NAMES.completionOn]: yup.string().date().nullable(),
      [EVENT_INPUT_NAMES.webReleaseOn]: yup.string().date().nullable(),
      [PREPARATION_ITEM_INPUT_NAMES.documentPlanning]: yup
        .number()
        .nullable()
        .transform((v) => (Number.isNaN(Number(v)) || Number(v) === 0 ? null : Number(v)))
        .default(null),
      [PREPARATION_ITEM_INPUT_NAMES.productionSchedule]: yup
        .number()
        .nullable()
        .transform((v) => (Number.isNaN(Number(v)) ? null : Number(v)))
        .default(null),
      [PREPARATION_ITEM_INPUT_NAMES.deliveryDestination]: yup.string().nullable().max(50),
      [PREPARATION_ITEM_INPUT_NAMES.bmsRegistration]: yup.string().nullable().max(20),
      [PREPARATION_ITEM_INPUT_NAMES.printSpecification]: yup
        .number()
        .nullable()
        .transform((v) => (Number.isNaN(Number(v)) || Number(v) === 0 ? null : Number(v)))
        .default(null),
      [PREPARATION_ITEM_INPUT_NAMES.arrangementInstruction]: yup
        .number()
        .nullable()
        .transform((v) => (Number.isNaN(Number(v)) || Number(v) === 0 ? null : Number(v)))
        .default(null),
      [PREPARATION_ITEM_INPUT_NAMES.massProductionDrawing]: yup
        .number()
        .nullable()
        .transform((v) => (Number.isNaN(Number(v)) || Number(v) === 0 ? null : Number(v)))
        .default(null),
      [PREPARATION_ITEM_INPUT_NAMES.desiredDeliveryOn]: yup.string().date().nullable(),
      [PREPARATION_ITEM_INPUT_NAMES.bodyIllustOrdering]: yup
        .number()
        .nullable()
        .transform((v) => (Number.isNaN(Number(v)) || Number(v) === 0 ? null : Number(v)))
        .default(null),
      [PREPARATION_ITEM_INPUT_NAMES.lightsIllustOrdering]: yup
        .number()
        .nullable()
        .transform((v) => (Number.isNaN(Number(v)) || Number(v) === 0 ? null : Number(v)))
        .default(null),
      [PREPARATION_ITEM_INPUT_NAMES.otherLanguageIllustOrdering]: yup
        .number()
        .nullable()
        .transform((v) => (Number.isNaN(Number(v)) || Number(v) === 0 ? null : Number(v)))
        .default(null),
      [PREPARATION_ITEM_INPUT_NAMES.vinRegistration]: yup
        .number()
        .nullable()
        .transform((v) => (Number.isNaN(Number(v)) || Number(v) === 0 ? null : Number(v)))
        .default(null),
      [PREPARATION_ITEM_INPUT_NAMES.webColorImage]: yup
        .number()
        .nullable()
        .transform((v) => (Number.isNaN(Number(v)) || Number(v) === 0 ? null : Number(v)))
        .default(null),
      [PREPARATION_ITEM_INPUT_NAMES.audioNaviColorImage]: yup
        .number()
        .nullable()
        .transform((v) => (Number.isNaN(Number(v)) || Number(v) === 0 ? null : Number(v)))
        .default(null),
      [PREPARATION_ITEM_INPUT_NAMES.webomTranslationSchedule]: yup
        .number()
        .nullable()
        .transform((v) => (Number.isNaN(Number(v)) || Number(v) === 0 ? null : Number(v)))
        .default(null),
      [PREPARATION_ITEM_INPUT_NAMES.generalPurchasingSlipNumber]: yup.string().nullable().half().max(20),
      [PREPARATION_ITEM_INPUT_NAMES.bmsSlipNumber]: yup.string().nullable().half().max(20),
    });

    // フォーム要素
    const {
      register,
      handleSubmit,
      control,
      setValue,
      getValues,
      reset,
      formState: { errors },
    } = useForm({
      defaultValues: getFormValues(basicInfo, event, preperationItem),
      resolver: yupResolver(schema),
    });

    // 制作管理表タブの組織ドロップダウンのデータ
    const organizationOptions = useMemo(() => getOptions(companyTrees, language === 'ja'), [companyTrees, language]);

    // スピナーモーダルを表示する
    const [displaySpinnerModal, setDisplaySpinnerModal] = useState(false);

    // ロックを取得する setInterval の返す ID
    const refLockIntervalId = useRef<NodeJS.Timer>();
    useEffect(() => () => clearInterval(refLockIntervalId.current), []);

    // beforeUnloadEvent（ブラウザを閉じようとしたときに、メッセージ表示）
    const beforeUnload = useCallback((e: BeforeUnloadEvent) => {
      e.returnValue = '';
    }, []);

    // コンポーネントが表示されなくなる時に、beforeunload イベントの解除
    useEffect(() => () => window.removeEventListener('beforeunload', beforeUnload), [beforeUnload]);

    // unload 関数（ブラウザを閉じるときに発生して、ロック解除するためのもの）
    const unload = useCallback(async () => {
      // 資料管理 ID が空の場合は何もしない
      if (productionId == null) return;

      await postWithKeepAlive(`/productions/cancel/${productionId}`, language);
    }, [language, productionId]);

    const refUnload = useRef<() => Promise<void>>();

    // コンポーネントが表示されなくなる時に、unload イベントの実行と解除
    useEffect(
      () => () => {
        if (refUnload.current != null) {
          void refUnload.current().then(() => {
            if (refUnload.current != null) {
              window.removeEventListener('unload', refUnload.current);
            }
          });
        }
      },
      []
    );

    // #region ロック関連

    // ロック API
    const options = useDefaultOptions(language);
    const { mutateAsync: lockAsync, error: lockError } = usePostProductionLock(options);

    // ロックエラーを表示するかどうか
    const [displayLockError, setDisplayLockError] = useState(false);

    // 他のタブがロックされていることのエラー表示
    const [displayAnotherTabLockingError, setDisplayAnotherTabLockingError] = useState(false);

    // ロックエラーが設定された場合は表示
    useEffect(() => {
      setDisplayLockError(lockError != null);
      setDisplaySpinnerModal(false);
    }, [lockError]);

    // 編集モードをやめる
    const finishEditing = useCallback(() => {
      // 編集モードをやめる
      changeToViewMode();

      // ロックの定期実行をやめる
      clearInterval(refLockIntervalId.current);

      // ブラウザを閉じた時などのイベントを解除
      window.removeEventListener('beforeunload', beforeUnload);
      if (refUnload.current != null) {
        window.removeEventListener('unload', refUnload.current);
      }

      // 親コンポーネントのデータリセット
      resetParent();

      // スピナーモーダルの非表示
      setDisplaySpinnerModal(false);
    }, [beforeUnload, changeToViewMode, resetParent]);

    // 編集モードにする
    const edit = useCallback(async () => {
      // 新規追加の場合
      if (productionId == null || refetch == null) {
        // ブラウザを閉じた時などのロック解除を設定
        window.addEventListener('beforeunload', beforeUnload);

        // 編集モードにする
        changeToEditMode();
        return;
      }

      // 編集モードのタブがある場合
      if (editModeTabIndex != null) {
        // 自身が編集モードのときは何もしない
        if (editModeTabIndex === 1) {
          return;
        }

        // 編集モードのタブがある場合はエラー表示
        setDisplayAnotherTabLockingError(true);
        return;
      }

      // スピナーモーダルの表示
      setDisplaySpinnerModal(true);

      // 成功した場合の処理
      const onSuccess = async () => {
        // 表示するデータをリフェッチする
        try {
          await refetch();

          // 定期的にロック API をたたく
          const lockInterval = parseInt(process.env.REACT_APP_GET_LOCK_INTERVAL ?? '10000', 10);
          refLockIntervalId.current = setInterval(() => {
            void lockAsync({ productionId }, { onError: finishEditing });
          }, lockInterval);

          // ブラウザを閉じた時などのロック解除を設定
          window.addEventListener('beforeunload', beforeUnload);
          refUnload.current = unload;
          window.addEventListener('unload', refUnload.current);

          // 編集モードにする
          changeToEditMode();
        } catch {
          // Do nothing
        } finally {
          // スピナーモーダルを非表示に
          setDisplaySpinnerModal(false);
        }
      };

      // 失敗時の処理（スピナーモーダルを非表示に）
      const onError = () => setDisplaySpinnerModal(false);

      // ロック API にアクセス
      await lockAsync({ productionId }, { onSuccess, onError });
    }, [productionId, refetch, editModeTabIndex, lockAsync, beforeUnload, changeToEditMode, unload, finishEditing]);

    // #endregion

    // #region キャンセル関連

    // キャンセル API
    const { mutateAsync: cancelAsync, error: cancelError } = usePostProductionCancel(options);

    // キャンセルエラーを表示するかどうか
    const [displayCancelError, setDisplayCancelError] = useState(false);

    // キャンセルエラーが設定された場合は表示
    useEffect(() => {
      setDisplayCancelError(cancelError != null);
      setDisplaySpinnerModal(false);
    }, [cancelError]);

    // キャンセルエラーが設定された場合は表示
    useEffect(() => {
      setDisplayCancelError(cancelError != null);
      setDisplaySpinnerModal(false);
    }, [cancelError]);

    // 編集モードをキャンセル
    const cancel = useCallback(async () => {
      // 自身が編集モードでないときは何もしない
      if (editModeTabIndex !== 1) return;

      // 新規追加時
      if (productionId == null || refetch == null) {
        // ブラウザを閉じた時のイベントを解除
        window.removeEventListener('beforeunload', beforeUnload);

        // 閲覧モードに
        changeToViewMode();
        return;
      }

      // スピナーモーダルの表示
      setDisplaySpinnerModal(true);

      // 成功時の処理（スピナーモーダルを非表示に）
      const onError = () => setDisplaySpinnerModal(false);

      // ロックキャンセル API をたたく
      await cancelAsync({ productionId }, { onSuccess: finishEditing, onError });
    }, [editModeTabIndex, productionId, refetch, cancelAsync, finishEditing, beforeUnload, changeToViewMode]);

    // #endregion

    // 保存時の確認モーダル（第一段階）
    const [showConfirmSavingModal, setShowConfirmSavingModal] = useState(false);

    // 保存時の確認モーダル（第二段階）
    const [confirmMessages2, setConfirmMessages2] = useState<{
      no: boolean;
      code: boolean;
      division: boolean;
      base: boolean;
    }>();

    // 保存 API
    const { mutateAsync: saveAsync, error: saveError } = usePostProductionSave(options);

    // 保存時エラーを表示するかどうか
    const [displaySaveError, setDisplaySaveError] = useState(false);

    // 保存時エラーが設定された場合は表示
    useEffect(() => {
      setDisplaySaveError(saveError != null);
      setDisplaySpinnerModal(false);
    }, [saveError]);

    // 登録 API
    const { mutateAsync: addAsync, error: addError } = usePostProductionAdd(options);

    // 追加時エラーを表示するかどうか
    const [displayAddError, setDisplayAddError] = useState(false);

    // 追加時エラーが設定された場合は表示
    useEffect(() => {
      setDisplayAddError(addError != null);
      setDisplaySpinnerModal(false);
    }, [addError]);

    // 保存完了メッセージ表示するかどうか
    const [displayCompleteSavingMessage, setDisplayCompleteSavingMessage] = useState(false);

    // データ送信実行
    const onSubmitMain = useCallback(async () => {
      setDisplaySpinnerModal(true);
      await handleSubmit(async (data: FormValues) => {
        const modelUserIds =
          data.model_ids != null
            ? data.model_ids.flatMap((x) => {
                const splited = x.split('_');
                return splited.length === 2 ? parseInt(splited[1], 10) : [];
              })
            : [];
        const originalWorkUserIds =
          data.original_work_ids != null
            ? data.original_work_ids.flatMap((x) => {
                const splited = x.split('_');
                return splited.length === 2 ? parseInt(splited[1], 10) : [];
              })
            : [];
        const scheduleSharingUserIds =
          data.schedule_sharing_ids != null
            ? data.schedule_sharing_ids.flatMap((x) => {
                const splited = x.split('_');
                return splited[0] === 'user' && splited.length === 2 ? parseInt(splited[1], 10) : [];
              })
            : [];
        const scheduleSharingCompanyIds =
          data.schedule_sharing_ids != null
            ? data.schedule_sharing_ids.flatMap((x) => {
                const splited = x.split('_');
                return splited[0] === 'company' && splited.length === 2 ? parseInt(splited[1], 10) : [];
              })
            : [];
        const nextBasicInfo: ProductionBasicInfo = {
          production_id: productionId != null ? productionId : undefined,
          production_no: data.production_no,
          production_division_id: selectedDivisionId,
          title: data.title,
          sales_my: data.sales_my,
          development_my: data.development_my,
          model_name: data.model_name,
          code: data.code,
          door_ids: data.door_ids,
          engine_ids: data.engine_ids,
          destination_ids: data.destination_ids,
          factory_ids: data.factory_ids,
          maker_company_id: data.maker_company_id,
          development_scale_id: data.development_scale_id,
          supplimentary_infomation: data.supplimentary_infomation,
          model_user_ids: modelUserIds,
          secret_management_number: data.secret_management_number,
          language_id: data.language_id,
          development_base_ids: data.development_base_ids,
          order_detail: data.order_detail,
          production_process_id: data.production_process_id,
          editing_method: data.editing_method,
          base_production_no: data.base_production_no,
          other_language_id: data.other_language_id,
          other_language_local: data.other_language_local,
          translation_difference_content_id: data.translation_difference_content_id,
          other_language_illust_id: data.other_language_illust_id,
          three_dimensions_illust_id: data.three_dimensions_illust_id,
          delivery_pdf_size: data.delivery_pdf_size,
          confidential_document_trust_permission: data.confidential_document_trust_permission,
          dmc_no: data.dmc_no,
          remarks: data.remarks,
          original_work_user_ids: originalWorkUserIds,
          schedule_sharing_user_ids: scheduleSharingUserIds,
          schedule_sharing_company_ids: scheduleSharingCompanyIds,
          parent_production_no: data.parent_production_no,
          count_target: data.count_target,
          correction_before_proofreading: data.correction_before_proofreading,
        };
        const nextEvent: ProductionEvent = {
          production_id: productionId != null ? productionId : undefined,
          kick_off: data.kick_off,
          service_study: data.service_study,
          development_department_check: data.development_department_check,
          subsidary_check: data.subsidary_check,
          vehicle_coverage: data.vehicle_coverage,
          validation: data.validation,
          slip_due_on: data.slip_due_on,
          material_provision_on: data.material_provision_on,
          machine_confirmation_start_on: data.machine_confirmation_start_on,
          data_item_completion_on: data.data_item_completion_on,
          data_sending_on: data.data_sending_on,
          translation_completion_on: data.translation_completion_on,
          scheduled_delivery_on: data.scheduled_delivery_on,
          download_location: data.download_location,
          g1_preparation_end_on: data.g1_preparation_end_on,
          g1_confirmation_end_on: data.g1_confirmation_end_on,
          completion_on: data.completion_on,
          web_release_on: data.web_release_on,
        };
        const nextPreperationItem: ProductionPreparationItem = {
          production_id: productionId != null ? productionId : undefined,
          document_planning: data.document_planning,
          production_schedule: data.production_schedule,
          delivery_destination: data.delivery_destination,
          bms_registration: data.bms_registration,
          print_specification: data.print_specification,
          arrangement_instruction: data.arrangement_instruction,
          mass_production_drawing: data.mass_production_drawing,
          desired_delivery_on: data.desired_delivery_on,
          body_illust_ordering: data.body_illust_ordering,
          lights_illust_ordering: data.lights_illust_ordering,
          other_language_illust_ordering: data.other_language_illust_ordering,
          vin_registration: data.vin_registration,
          web_color_image: data.web_color_image,
          audio_navi_color_image: data.audio_navi_color_image,
          webom_translation_schedule: data.webom_translation_schedule,
          general_purchasing_slip_number: data.general_purchasing_slip_number,
          bms_slip_number: data.bms_slip_number,
        };
        const nextProductionDetail: ProductionDetail = {
          production_basic_info: nextBasicInfo,
          production_event: nextEvent,
          production_preparation_item: nextPreperationItem,
        };

        if (productionId != null && refetch != null) {
          // 成功時は閲覧モードにする
          const onSuccess = async () => {
            // ブラウザを閉じた時などのイベントを解除
            window.removeEventListener('beforeunload', beforeUnload);
            if (refUnload.current != null) {
              window.removeEventListener('unload', refUnload.current);
            }

            try {
              // 保存成功したらリフェッチする
              await refetch();

              // 編集状態を解除
              changeToViewMode();

              // ロック API の呼び出しを解除
              clearInterval(refLockIntervalId.current);

              // 保存完了したことをメッセージ表示
              setDisplayCompleteSavingMessage(true);
            } catch {
              // Do nothing
            } finally {
              // スピナーモーダルを非表示
              setDisplaySpinnerModal(false);
            }
          };

          // 成功失敗共通の処理（スピナーモーダルを非表示に）
          const onError = () => setDisplaySpinnerModal(false);

          // 保存の実行
          await saveAsync({ data: nextProductionDetail }, { onSuccess, onError });
        } else {
          // 成功時は画面遷移する
          const onSuccess = (responseData: AxiosResponse<PostProductionAdd200, unknown>) => {
            if (responseData?.data?.production_id == null) return;
            navigate(`/${language}/productions/edit/${responseData.data.production_id.toString()}`);
          };

          // 成功失敗共通の処理（スピナーモーダルを非表示に）
          const onError = () => setDisplaySpinnerModal(false);

          // 登録の実行
          await addAsync({ data: nextProductionDetail }, { onSuccess, onError });
        }
      })();
    }, [
      handleSubmit,
      productionId,
      selectedDivisionId,
      refetch,
      saveAsync,
      beforeUnload,
      changeToViewMode,
      addAsync,
      navigate,
      language,
    ]);

    // 発注書出力
    const optionsForBinary = useOptionsForBinary(language);
    const {
      mutateAsync: _downloadAsync,
      isError: downloadIsError,
      error: downloadError,
    } = usePostProductionOrderSheetsDownload<AxiosError<Blob>>(optionsForBinary);

    const [displayDownloadError, setDisplayDownloadError] = useState(false);

    useEffect(() => {
      setDisplayDownloadError(downloadIsError);
    }, [downloadIsError]);

    // ダウンロード処理
    const downloadAsync = useCallback(
      async (newValue: { value: number; label: string } | null) => {
        setDisplaySpinnerModal(true);
        if (newValue != null && productionId != null) {
          await _downloadAsync(
            { productionId, masterId: newValue.value },
            {
              onSuccess: (response) => {
                executeDownload(response);
                setDisplaySpinnerModal(false);
              },
              onError: () => {
                setDisplaySpinnerModal(false);
              },
            }
          );
        }
      },
      [productionId, _downloadAsync]
    );

    // 親コンポーネントからの操作
    useImperativeHandle(ref, () => ({
      // フォームの送信
      submit: async () => {
        await handleSubmit(() => {
          setShowConfirmSavingModal(true);
        })();
      },
      // フォームのリフレッシュ
      refresh: () => {
        reset(getFormValues(basicInfo, event, preperationItem));
      },
    }));

    // 必要なデータが取得できなかった場合
    if (
      masters.model_names == null ||
      masters.codes == null ||
      masters.doors == null ||
      masters.engines == null ||
      masters.destinations == null ||
      masters.factories == null ||
      masters.development_bases == null ||
      masters.production_divisions == null ||
      masters.development_scales == null ||
      masters.languages == null ||
      masters.production_processes == null ||
      masters.production_statuses == null ||
      masters.schedule_divisions == null ||
      masters.companies == null ||
      masters.master_settings == null
    ) {
      return <NoDataComponent />;
    }

    return (
      <>
        <h3 className="mdh3 fs15">
          <span>
            {t('基本情報')}
            <button type="button" className="text-button" onClick={() => setOpen((pre) => !pre)}>
              <i className={`fa-solid ${open ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
            </button>
          </span>
        </h3>
        <SlideToggle open={open}>
          <div className="base-master">
            {/* 上部のボタン類 */}
            <div className="master-navi">
              <ul>
                {/* 発注書出力ボタン */}
                <li className="">
                  <Select
                    // 選択肢は資料区分によって異なる
                    options={getOutputOptions(selectedDivisionId, t)}
                    placeholder={
                      <>
                        <i className="fa-solid fa-download" />
                        <span>{t('発注書出力')}</span>
                      </>
                    }
                    // インジケーターは利用しない
                    components={{
                      IndicatorsContainer: () => null,
                      IndicatorSeparator: () => null,
                    }}
                    value={null} // オプションが選択されてもプレースフォルダを表示
                    isSearchable={false}
                    styles={{
                      // 無駄な余白を除去
                      valueContainer: (base) => ({ ...base, padding: 0 }),
                      // 初期状態で minHeight=38px が設定されているため解除
                      control: (base) => ({ ...base, border: 'none', minHeight: undefined }),
                    }}
                    onChange={(newValue) => {
                      // オプションが選択したらダウンロードを実行
                      void downloadAsync(newValue);
                    }}
                    closeMenuOnSelect
                    blurInputOnSelect // 選択後にフォーカス解除（こうしないと、連続で押すことができなくなる）
                    isDisabled={productionId == null} // 新規登録時は押下不可
                  />
                </li>
                {/* 編集ボタン */}
                <li className="">
                  <button type="button" className="text-button" onClick={edit} disabled={editable}>
                    <i className="fa-solid fa-pen-to-square" />
                    <span>{t('編集')}</span>
                  </button>
                </li>
                {/* キャンセルボタン */}
                <li className="">
                  <CancelButton editable={editable} cancel={cancel} />
                </li>
              </ul>
            </div>
            {/* 以下フォーム部分 */}
            <form>
              <fieldset disabled={!editable} className="fieldset-reset">
                <div className="basicdatafr1">
                  {/* 一列目 */}
                  <div className="basicdatafr1-1">
                    <h4>
                      <label>
                        <i className="fa-sharp fa-solid fa-circle" />
                        {t('基本情報')}
                      </label>
                    </h4>
                    <ul>
                      <InputGroup label={t('資料管理No*')} name="production_no" errors={errors} register={register} />
                      <li>
                        <label htmlFor="title">{t('資料名')}</label>
                        <span>
                          <input type="text" id="title" className="widths" {...register('title')} />
                          <button
                            type="button"
                            className="widthsbtn cursor text-button"
                            disabled={!editable}
                            onClick={() => {
                              const values = getValues();

                              // 現在の値に基づいて資料名を設定
                              // 資料名の命名規則：資料区分＋機種名＋ドア数※＋エンジンタイプ※＋仕向＋資料管理No.＋工場名＋原作会社名＋HM担当者名＋（FMC,NEW,MMC,YMC,改訂）
                              const divisionName = (masters.production_divisions ?? []).find(
                                (x) => x.production_division_id === selectedDivisionId
                              )?.production_division_name;
                              const modelName = values.model_name;
                              const doorNames = (values.door_ids ?? []).map(
                                (x) => (masters.doors ?? []).find((y) => y.door_id === x)?.door_name
                              );
                              const engineNames = (values.engine_ids ?? []).map(
                                (x) => (masters.engines ?? []).find((y) => y.engine_id === x)?.engine_name
                              );
                              const destinationNames = (values.destination_ids ?? []).map(
                                (x) =>
                                  (masters.destinations ?? []).find((y) => y.destination_id === x)?.destination_name
                              );
                              const productionNo = values.production_no;
                              const factoryNames = (values.factory_ids ?? []).map(
                                (x) => (masters.factories ?? []).find((y) => y.factory_id === x)?.factory_name
                              );
                              const makerCompanyName = (masters.companies ?? []).find(
                                (x) => x.company_id === values.maker_company_id
                              )?.company_name;
                              // HM担当者名
                              const modelUserNames = (values.model_ids ?? []).map(
                                (x) => (organizationOptions ?? []).find((y) => y.value === x)?.label
                              );
                              // FMC,NEW,MMC,YMC,改訂
                              const developmentScaleName = (masters.development_scales ?? []).find(
                                (x) => x.development_scale_id === values.development_scale_id
                              )?.development_scale_name;

                              const titleElements = [
                                divisionName,
                                modelName,
                                ...doorNames,
                                ...engineNames,
                                ...destinationNames,
                                productionNo,
                                ...factoryNames,
                                makerCompanyName,
                                ...modelUserNames,
                                developmentScaleName,
                              ].flatMap((x) => (x != null ? x : []));
                              const nextTitle = titleElements.join(' ');
                              setValue('title', nextTitle);
                            }}
                          >
                            <i className="fa-solid fa-circle-plus" />
                            {t('作成')}
                          </button>
                        </span>
                      </li>
                      {errors.title?.message != null && (
                        <li>
                          <span />
                          <span className="form-error">{errors.title.message.toString()}</span>
                        </li>
                      )}
                      <SingleSelectGroup
                        label={t('販売MY*')}
                        name="sales_my"
                        allowEmpty
                        options={Array.from(Array(31).keys(), (x) => x + 2020)}
                        errors={errors}
                        register={register}
                        getValue={(x) => x}
                        getLabel={(x) => x}
                      />
                      <SingleSelectGroup
                        label={t('開発MY*')}
                        name="development_my"
                        allowEmpty
                        options={Array.from(Array(61).keys(), (x) => x * 0.5 + 2020)}
                        errors={errors}
                        register={register}
                        getValue={(x) => x}
                        getLabel={(x) => x}
                      />
                      <SingleSelectGroup
                        label={t('機種名')}
                        name="model_name"
                        allowEmpty
                        options={masters.model_names}
                        errors={errors}
                        register={register}
                        getValue={(x) => x}
                        getLabel={(x) => x}
                      />
                      <SingleSelectGroup
                        label={t('Code')}
                        name="code"
                        allowEmpty
                        options={masters.codes}
                        errors={errors}
                        register={register}
                        getValue={(x) => x}
                        getLabel={(x) => x}
                      />
                      <MultiSelectGroup
                        label={t('ドア数')}
                        name="door_ids"
                        options={masters.doors}
                        control={control}
                        isDisabled={!editable}
                        errors={errors}
                        getValue={(x) => x.door_id}
                        getLabel={(x) => x.door_name}
                      />
                      <MultiSelectGroup
                        label={t('エンジン')}
                        name="engine_ids"
                        options={masters.engines}
                        control={control}
                        isDisabled={!editable}
                        errors={errors}
                        getValue={(x) => x.engine_id}
                        getLabel={(x) => x.engine_name}
                      />
                      <MultiSelectGroup
                        label={t('仕向')}
                        name="destination_ids"
                        options={masters.destinations}
                        control={control}
                        isDisabled={!editable}
                        errors={errors}
                        getValue={(x) => x.destination_id}
                        getLabel={(x) => x.destination_name}
                      />
                      <MultiSelectGroup
                        label={t('工場')}
                        name="factory_ids"
                        options={masters.factories}
                        control={control}
                        isDisabled={!editable}
                        errors={errors}
                        getValue={(x) => x.factory_id}
                        getLabel={(x) => x.factory_name}
                      />
                      <SingleSelectGroup
                        label={t('原作メーカー')}
                        name="maker_company_id"
                        allowEmpty
                        options={masters.companies}
                        errors={errors}
                        register={register}
                        getValue={(x) => x.company_id}
                        getLabel={(x) => x.company_name}
                      />
                      <SingleSelectGroup
                        label={t('開発規模')}
                        name="development_scale_id"
                        allowEmpty
                        options={masters.development_scales}
                        errors={errors}
                        register={register}
                        getValue={(x) => x.development_scale_id}
                        getLabel={(x) => x.development_scale_name}
                      />
                      <InputGroup
                        label={t('補足情報')}
                        name="supplimentary_infomation"
                        errors={errors}
                        register={register}
                      />
                      <OrganizationTreeMultiSelectGroup
                        label={t('機種担当')}
                        name="model_ids"
                        control={control}
                        options={organizationOptions}
                        selectableTypes={['user']}
                        isDisabled={!editable}
                        errors={errors}
                      />
                      <InputGroup
                        label={t('機密管理No')}
                        name="secret_management_number"
                        errors={errors}
                        register={register}
                      />
                      <SingleSelectGroup
                        label={t('言語')}
                        name="language_id"
                        allowEmpty={false}
                        options={masters.languages}
                        errors={errors}
                        register={register}
                        getValue={(x) => x.language_id}
                        getLabel={(x) => x.language_name}
                      />
                      <MultiSelectGroup
                        label={t('開発拠点')}
                        name="development_base_ids"
                        options={masters.development_bases}
                        control={control}
                        isDisabled={!editable}
                        errors={errors}
                        getValue={(x) => x.development_base_id}
                        getLabel={(x) => x.development_base_name}
                      />
                      <InputGroup label={t('発注内容')} name="order_detail" errors={errors} register={register} />
                      <SingleSelectGroup
                        label={t('制作工程')}
                        name="production_process_id"
                        allowEmpty
                        options={masters.production_processes}
                        errors={errors}
                        register={register}
                        getValue={(x) => x.production_process_id}
                        getLabel={(x) => x.production_process_name}
                      />
                    </ul>
                    <p className="additiontxtcx">{t('* は必須入力項目です。')}</p>
                  </div>
                  {/* 二列目 */}
                  <div className="basicdatafr1-2">
                    <ul>
                      <InputGroup label={t('編集方法')} name="editing_method" errors={errors} register={register} />
                      <InputGroup label={t('ベース本')} name="base_production_no" errors={errors} register={register} />
                      <SingleSelectGroup
                        label={t('他言語(HS)')}
                        name="other_language_id"
                        allowEmpty
                        options={masters.master_settings.filter((x) => x.master_id === 21)}
                        errors={errors}
                        register={register}
                        getValue={(x) => x.setting_value_id}
                        getLabel={(x) => x.value}
                      />
                      <InputGroup
                        label={t('他言語(現地)')}
                        name="other_language_local"
                        errors={errors}
                        register={register}
                      />
                      <SingleSelectGroup
                        label={t('翻訳用差分データ')}
                        name="translation_difference_content_id"
                        allowEmpty
                        options={masters.master_settings.filter((x) => x.master_id === 22)}
                        errors={errors}
                        register={register}
                        getValue={(x) => x.setting_value_id}
                        getLabel={(x) => x.value}
                      />
                      <SingleSelectGroup
                        label={t('多言語用イラスト')}
                        name="other_language_illust_id"
                        allowEmpty
                        options={masters.master_settings.filter((x) => x.master_id === 23)}
                        errors={errors}
                        register={register}
                        getValue={(x) => x.setting_value_id}
                        getLabel={(x) => x.value}
                      />
                      <SingleSelectGroup
                        label={t('3Dイラスト')}
                        name="three_dimensions_illust_id"
                        allowEmpty
                        options={masters.master_settings.filter((x) => x.master_id === 24)}
                        errors={errors}
                        register={register}
                        getValue={(x) => x.setting_value_id}
                        getLabel={(x) => x.value}
                      />
                      <SingleSelectGroup
                        label={t('納品PDFサイズ')}
                        name="delivery_pdf_size"
                        allowEmpty
                        options={[
                          t('100%PDF/ WebPDF').toString(),
                          t('100%PDF/ 88%PDF/ WebPDF').toString(),
                          t('100%PDF/ 88%PDF').toString(),
                        ]}
                        errors={errors}
                        register={register}
                        getValue={(x) => x}
                        getLabel={(x) => x}
                      />
                      <InputGroup
                        label={t('機密文書預託許可')}
                        name="confidential_document_trust_permission"
                        errors={errors}
                        register={register}
                      />
                      {[4, 10].includes(selectedDivisionId) && (
                        <InputGroup label={t('DMC資料管理番号')} name="dmc_no" errors={errors} register={register} />
                      )}
                    </ul>
                    <h4 className="mt20">
                      <label>
                        <i className="fa-sharp fa-solid fa-circle" />
                        {t('イベント')}
                      </label>
                    </h4>
                    <ul>
                      {[1, 2, 3, 5, 6, 7, 8, 9, 11, 12, 13, 14].includes(selectedDivisionId) && (
                        <SingleSelectGroup
                          label={t('キックオフ会')}
                          name="kick_off"
                          allowEmpty
                          options={masters.master_settings.filter((x) => x.master_id === 25)}
                          errors={errors}
                          register={register}
                          getValue={(x) => x.setting_value_id}
                          getLabel={(x) => x.value}
                        />
                      )}
                      {[1, 2, 3, 6, 7, 8, 9, 12, 13, 14].includes(selectedDivisionId) && (
                        <>
                          <SingleSelectGroup
                            label={t('サービス勉強会')}
                            name="service_study"
                            allowEmpty
                            options={masters.master_settings.filter((x) => x.master_id === 26)}
                            errors={errors}
                            register={register}
                            getValue={(x) => x.setting_value_id}
                            getLabel={(x) => x.value}
                          />
                          <SingleSelectGroup
                            label={t('開発部門チェック')}
                            name="development_department_check"
                            allowEmpty
                            options={masters.master_settings.filter((x) => x.master_id === 27)}
                            errors={errors}
                            register={register}
                            getValue={(x) => x.setting_value_id}
                            getLabel={(x) => x.value}
                          />
                          <SingleSelectGroup
                            label={t('現法チェック')}
                            name="subsidary_check"
                            allowEmpty
                            options={masters.master_settings.filter((x) => x.master_id === 28)}
                            errors={errors}
                            register={register}
                            getValue={(x) => x.setting_value_id}
                            getLabel={(x) => x.value}
                          />
                          <SingleSelectGroup
                            label={t('実車取材')}
                            name="vehicle_coverage"
                            allowEmpty
                            options={masters.master_settings.filter((x) => x.master_id === 29)}
                            errors={errors}
                            register={register}
                            getValue={(x) => x.setting_value_id}
                            getLabel={(x) => x.value}
                          />
                          <SingleSelectGroup
                            label={t('バリデーション')}
                            name="validation"
                            allowEmpty
                            options={masters.master_settings.filter((x) => x.master_id === 30)}
                            errors={errors}
                            register={register}
                            getValue={(x) => x.setting_value_id}
                            getLabel={(x) => x.value}
                          />
                          <DatePickerGroup
                            label={t('伝票納期')}
                            name="slip_due_on"
                            isDisabled={!editable}
                            control={control}
                            errors={errors}
                          />
                        </>
                      )}
                      {[4, 10].includes(selectedDivisionId) && (
                        <>
                          <DatePickerGroup
                            label={t('素材提供日')}
                            name="material_provision_on"
                            isDisabled={!editable}
                            control={control}
                            errors={errors}
                          />
                          <DatePickerGroup
                            label={t('実機確認開始日')}
                            name="machine_confirmation_start_on"
                            isDisabled={!editable}
                            control={control}
                            errors={errors}
                          />
                          <DatePickerGroup
                            label={t('データ完成日')}
                            name="data_item_completion_on"
                            isDisabled={!editable}
                            control={control}
                            errors={errors}
                          />
                          <DatePickerGroup
                            label={t('伝票納期')}
                            name="slip_due_on"
                            isDisabled={!editable}
                            control={control}
                            errors={errors}
                          />
                          <DatePickerGroup
                            label={t('現法データ送付日')}
                            name="data_sending_on"
                            isDisabled={!editable}
                            control={control}
                            errors={errors}
                          />
                          <DatePickerGroup
                            label={t('現法 他言語翻訳完了日')}
                            name="translation_completion_on"
                            isDisabled={!editable}
                            control={control}
                            errors={errors}
                          />
                          <DatePickerGroup
                            label={t('配信設定予定日')}
                            name="scheduled_delivery_on"
                            isDisabled={!editable}
                            control={control}
                            errors={errors}
                          />
                          <InputGroup
                            label={t('ダウンロード場所')}
                            name="download_location"
                            errors={errors}
                            register={register}
                          />
                        </>
                      )}
                      {[5, 11].includes(selectedDivisionId) && (
                        <>
                          <DatePickerGroup
                            label={t('G1準備終了日')}
                            name="g1_preparation_end_on"
                            isDisabled={!editable}
                            control={control}
                            errors={errors}
                          />
                          <DatePickerGroup
                            label={t('G1確認終了日')}
                            name="g1_confirmation_end_on"
                            isDisabled={!editable}
                            control={control}
                            errors={errors}
                          />
                          <DatePickerGroup
                            label={t('完成日')}
                            name="completion_on"
                            isDisabled={!editable}
                            control={control}
                            errors={errors}
                          />
                          <DatePickerGroup
                            label={t('Web公開日')}
                            name="web_release_on"
                            isDisabled={!editable}
                            control={control}
                            errors={errors}
                          />
                        </>
                      )}
                    </ul>
                  </div>
                  {/* 三列目 */}
                  <div className="basicdatafr1-3" style={{ zIndex: 10 }}>
                    <h4>
                      <label>
                        <i className="fa-sharp fa-solid fa-circle" />
                        {t('発注時HS準備項目')}
                      </label>
                    </h4>
                    <ul>
                      {[1, 2, 3, 6, 7, 8, 9, 12, 13, 14].includes(selectedDivisionId) && (
                        <>
                          <SingleSelectGroup
                            label={t('資料企画')}
                            name="document_planning"
                            allowEmpty
                            errors={errors}
                            options={[
                              { value: 1, label: t('有').toString() },
                              { value: 2, label: t('無').toString() },
                            ]}
                            register={register}
                            getValue={(x) => x.value}
                            getLabel={(x) => x.label}
                          />
                          <SingleSelectGroup
                            label={t('制作日程表')}
                            name="production_schedule"
                            allowEmpty
                            options={[
                              { value: 1, label: t('済').toString() },
                              { value: 0, label: t('未実施').toString() },
                            ]}
                            errors={errors}
                            register={register}
                            getValue={(x) => x.value}
                            getLabel={(x) => x.label}
                          />
                          <InputGroup
                            label={t('納品先')}
                            name="delivery_destination"
                            errors={errors}
                            register={register}
                          />
                          <InputGroup
                            label={t('BMS登録')}
                            name="bms_registration"
                            errors={errors}
                            register={register}
                          />
                          <SingleSelectGroup
                            label={t('印刷物仕様書')}
                            name="print_specification"
                            allowEmpty
                            options={masters.master_settings.filter((x) => x.master_id === 31)}
                            errors={errors}
                            register={register}
                            getValue={(x) => x.setting_value_id}
                            getLabel={(x) => x.value}
                          />
                          <SingleSelectGroup
                            label={t('手配指示関連')}
                            name="arrangement_instruction"
                            allowEmpty
                            options={masters.master_settings.filter((x) => x.master_id === 32)}
                            errors={errors}
                            register={register}
                            getValue={(x) => x.setting_value_id}
                            getLabel={(x) => x.value}
                          />
                          <SingleSelectGroup
                            label={t('量産図発行')}
                            name="mass_production_drawing"
                            allowEmpty
                            options={masters.master_settings.filter((x) => x.master_id === 33)}
                            errors={errors}
                            register={register}
                            getValue={(x) => x.setting_value_id}
                            getLabel={(x) => x.value}
                          />
                          <DatePickerGroup
                            label={t('希望納期')}
                            name="desired_delivery_on"
                            isDisabled={!editable}
                            control={control}
                            errors={errors}
                          />
                        </>
                      )}
                      {[4, 10].includes(selectedDivisionId) && (
                        <>
                          <SingleSelectGroup
                            label={t('dOM用車体イラスト制作の発注')}
                            name="body_illust_ordering"
                            allowEmpty
                            options={masters.master_settings.filter((x) => x.master_id === 34)}
                            errors={errors}
                            register={register}
                            getValue={(x) => x.setting_value_id}
                            getLabel={(x) => x.value}
                          />
                          <SingleSelectGroup
                            label={t('警告灯・表示灯カラーイラスト制作の発注')}
                            name="lights_illust_ordering"
                            allowEmpty
                            options={masters.master_settings.filter((x) => x.master_id === 35)}
                            errors={errors}
                            register={register}
                            getValue={(x) => x.setting_value_id}
                            getLabel={(x) => x.value}
                          />
                          <SingleSelectGroup
                            label={t('他言語イラスト制作の発注')}
                            name="other_language_illust_ordering"
                            allowEmpty
                            options={masters.master_settings.filter((x) => x.master_id === 36)}
                            errors={errors}
                            register={register}
                            getValue={(x) => x.setting_value_id}
                            getLabel={(x) => x.value}
                          />
                          <SingleSelectGroup
                            label={t('事前VIN登録')}
                            name="vin_registration"
                            allowEmpty
                            options={masters.master_settings.filter((x) => x.master_id === 37)}
                            errors={errors}
                            register={register}
                            getValue={(x) => x.setting_value_id}
                            getLabel={(x) => x.value}
                          />
                        </>
                      )}
                      {[5, 11].includes(selectedDivisionId) && (
                        <>
                          <SingleSelectGroup
                            label={t('Web内カラー化画像の作成')}
                            name="web_color_image"
                            allowEmpty
                            options={masters.master_settings.filter((x) => x.master_id === 38)}
                            errors={errors}
                            register={register}
                            getValue={(x) => x.setting_value_id}
                            getLabel={(x) => x.value}
                          />
                          <SingleSelectGroup
                            label={t('Audio・Navi画面カラー画像作成')}
                            name="audio_navi_color_image"
                            allowEmpty
                            options={masters.master_settings.filter((x) => x.master_id === 39)}
                            errors={errors}
                            register={register}
                            getValue={(x) => x.setting_value_id}
                            getLabel={(x) => x.value}
                          />
                          <SingleSelectGroup
                            label={t('WebOM翻訳日程表')}
                            name="webom_translation_schedule"
                            allowEmpty
                            options={masters.master_settings.filter((x) => x.master_id === 40)}
                            errors={errors}
                            register={register}
                            getValue={(x) => x.setting_value_id}
                            getLabel={(x) => x.value}
                          />
                        </>
                      )}
                      <InputGroup
                        label={t('一般購買伝票No')}
                        name="general_purchasing_slip_number"
                        errors={errors}
                        register={register}
                      />
                      <InputGroup label={t('BMS伝票No')} name="bms_slip_number" errors={errors} register={register} />
                    </ul>
                    <h4 className="mt20">
                      <label>
                        <i className="fa-sharp fa-solid fa-circle" />
                        {t('権限/関連資料')}
                      </label>
                    </h4>
                    <ul>
                      <OrganizationTreeMultiSelectGroup
                        label={t('原作推進担当者')}
                        name="original_work_ids"
                        control={control}
                        options={organizationOptions}
                        selectableTypes={['user']}
                        isDisabled={!editable}
                        errors={errors}
                      />
                      <OrganizationTreeMultiSelectGroup
                        label={t('日程共有先')}
                        name="schedule_sharing_ids"
                        control={control}
                        options={organizationOptions}
                        selectableTypes={['company', 'user']}
                        isDisabled={!editable}
                        errors={errors}
                      />
                      <InputGroup
                        label={t('親資料No')}
                        name="parent_production_no"
                        errors={errors}
                        register={register}
                      />
                      <SingleSelectGroup
                        label={t('カウント対象')}
                        name="count_target"
                        allowEmpty
                        options={[{ value: 1, label: t('〇').toString() }]}
                        errors={errors}
                        register={register}
                        getValue={(x) => x.value}
                        getLabel={(x) => x.label}
                      />
                      <InputGroup
                        label={t('校了後修正')}
                        name="correction_before_proofreading"
                        errors={errors}
                        isDisabled
                        register={register}
                      />
                    </ul>
                  </div>
                </div>
                {/* 備考欄 */}
                <div className="basicdatafr2">
                  <div className="bd-area">
                    <label htmlFor="remarks">
                      <i className="fa-sharp fa-solid fa-circle" />
                      {t('備考')}
                    </label>
                    <textarea id="remarks" rows={5} cols={33} {...register('remarks')} />
                    {errors.remarks?.message != null && (
                      <p className="form-error">{errors.remarks.message.toString()}</p>
                    )}
                  </div>
                </div>
              </fieldset>
            </form>
            {/* 親子関係リスト */}
            <ParentChildList parentChildList={parentChildList} />
          </div>
        </SlideToggle>
        {/* 一段階目の確認メッセージ */}
        {showConfirmSavingModal && (
          <ConfirmModal
            onClickOk={async () => {
              setShowConfirmSavingModal(false);
              await handleSubmit(async (data) => {
                // XX と 翻訳(XX) は同じものとみなす
                // OM 系と Navi 系も同じものとみなす
                let preDivisionType = null;
                if (basicInfo.production_division_id != null) {
                  preDivisionType = basicInfo.production_division_id;
                  if ([1, 2, 3, 13].includes(preDivisionType)) {
                    preDivisionType = 1;
                  } else if ([7, 8, 9, 14].includes(preDivisionType)) {
                    preDivisionType = 7;
                  }
                }
                let nextDivisionType = selectedDivisionId;
                if ([1, 2, 3, 13].includes(nextDivisionType)) {
                  nextDivisionType = 1;
                } else if ([7, 8, 9, 14].includes(nextDivisionType)) {
                  nextDivisionType = 7;
                }

                // 他の部分に影響がある変更がされていた場合は、再度ワーニングメッセージを表示
                if (
                  productionId != null &&
                  (data.production_no !== basicInfo.production_no ||
                    (data.code ?? '') !== (basicInfo.code ?? '') ||
                    preDivisionType !== nextDivisionType ||
                    (basicInfo.base_production_no != null && data.base_production_no !== basicInfo.base_production_no))
                ) {
                  setConfirmMessages2({
                    no: data.production_no !== basicInfo.production_no,
                    code: (data.code ?? '') !== (basicInfo.code ?? ''),
                    division: preDivisionType !== nextDivisionType,
                    base:
                      basicInfo.base_production_no != null && data.base_production_no !== basicInfo.base_production_no,
                  });
                  return;
                }
                await onSubmitMain();
              })();
            }}
            onClickCancel={() => setShowConfirmSavingModal(false)}
          >
            <div>{t('データを更新しますか？')}</div>
          </ConfirmModal>
        )}
        {/* 二段階目の確認メッセージ */}
        {confirmMessages2 != null && (
          <YesNoModal
            onClickYes={async () => {
              setConfirmMessages2(undefined);
              await onSubmitMain();
            }}
            onClickNo={() => {
              setConfirmMessages2(undefined);
            }}
          >
            {confirmMessages2.no && <div>{t('トス資料に保管されているファイルが削除されます。')}</div>}
            {confirmMessages2.code && (
              <div>
                {t(
                  'Code が変更されました。トス資料に格納されたファイルが削除されます。また、これまで所属していた資料管理 No. の一覧から離脱し、新しい資料管理 No. の一覧に追加されます。'
                )}
              </div>
            )}
            {confirmMessages2.division && (
              <div>
                {t('制作管理表タブ、日程編集タブ、確認依頼タブの情報が初期化されます。初期化する場合YESを選択。')}
              </div>
            )}
            {confirmMessages2.base && (
              <div>
                {t('ベース本が変更されました。原稿タブのF-List判定結果表示、および翻訳タブの翻訳履歴が削除されます。')}
              </div>
            )}
          </YesNoModal>
        )}
        {/* 他のタブがロック中であることを表示 */}
        {displayAnotherTabLockingError && editModeTabIndex != null && (
          <AnotherTabLockingErrorModal
            editModeTabIndex={editModeTabIndex}
            onClickOk={() => setDisplayAnotherTabLockingError(false)}
          />
        )}
        {/* ロックエラー表示 */}
        {displayLockError && <ErrorModal error={lockError} onClickOk={() => setDisplayLockError(false)} />}
        {/* ロックキャンセルエラー表示 */}
        {displayCancelError && <ErrorModal error={cancelError} onClickOk={() => setDisplayCancelError(false)} />}
        {/* 保存時のエラー表示 */}
        {displaySaveError && <ErrorModal error={saveError} onClickOk={() => setDisplaySaveError(false)} />}
        {/* 追加時のエラー表示 */}
        {displayAddError && <ErrorModal error={addError} onClickOk={() => setDisplayAddError(false)} />}
        {/* 発注書出力エラー */}
        {displayDownloadError && (
          <BlobErrorModal error={downloadError} onClickOk={() => setDisplayDownloadError(false)} />
        )}
        {/* 保存完了メッセージ */}
        {displayCompleteSavingMessage && (
          <OkModal onClickOk={() => setDisplayCompleteSavingMessage(false)}>
            <div>{t('保存しました。')}</div>
          </OkModal>
        )}
        {/* スピナーモーダル */}
        {displaySpinnerModal && <SpinnerModal />}
      </>
    );
  }
);

export default ProductionManagementTableTab;
